import { FC, ReactElement, useContext, useEffect, useState } from "react"
import { ZipProjectContext } from "./ZipProjectContext"
import { QESAlert, QESAlertsContainer, QESButton } from "@app/shared/qes-standards";
import { ProjectRunConfigurationStatus } from "../enumerators";
import { isProjectRunning, onRunProject } from "./utility";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { translateProjectChangeReason } from "../projectChangeReason";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";
import { ZipProjectPublishFlyout } from "./ZipProjectPublishFlyout";
import PendingProviderChangesSidePanelLink from "../servicing-provider/PendingProviderChangesSidePanelLink";
import { FeatureFlagContext, Features } from "../../feature-flags";

const runningBanner = (
  <QESAlert type="info" key="projectIsRunning" persist={true}>
    This project is in a read-only mode while processing updates. Some items may not appear while they are being worked on.
  </QESAlert>
);

export const ZipProjectAlertBanners: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const user = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const { state, dispatch } = projectContext;
  const [alertBanners, setAlertBanners] = useState<ReactElement[]>([]);
  const projectIsRunning = isProjectRunning(state.project);

  const renderUnprocessedProvidersSidePanelLink = (getTargets: boolean, getTerminations: boolean, projectId: number, processProviderChanges: (reprocessFailure: boolean) => Promise<void>) => {
    return (
      <PendingProviderChangesSidePanelLink
        getTargets={getTargets}
        getTerminations={getTerminations}
        processProviderChanges={processProviderChanges}
        projectId={projectId}
        resetStatusBanners={projectContext.onResetStatusBanners}
        isZipProject={true}
      />
    );
  }

  // all status/alert banner checks should be done in this useEffect
  // (or an abstracted function it calls) so we only set them in one place
  useEffect(() => {
    if (!state.project) {
      return;
    }
    const isShared = state.project.resource.isShared;
    const newAlertBanners: ReactElement[] = [];

    const termAdmin = user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && featureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
    const projectionProcessor = user.roles.includes(Roles.PROJECTION_PROCESSOR) && featureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
    const showProjectionMessage = state.project.hasUnprocessedProjectionProviders && projectionProcessor && !state.project.isRunning && state.runConfiguration && state.runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const renderUnprocessedProvidersSidePanelLinkElement = renderUnprocessedProvidersSidePanelLink(projectionProcessor, termAdmin, state.project.projectId, projectContext.onProcessProviderChanges);

    if (projectIsRunning) {
      newAlertBanners.push(runningBanner);
    } else if (!isShared) {
      if (!state.runConfiguration) {
        newAlertBanners.push(
          <QESAlert key="created" type="info" persist={true}>
            <strong>Thank you!</strong> Your project has been created, please run it.
            <QESButton
              className="ml-3"
              qesType="secondary"
              onClick={() => { onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId); }}
              qesTestId="zip-project-overview__run-project--no-config"><i className="fa fa-rocket" /> Run Now</QESButton>
          </QESAlert>
        );
      }

      if (state.runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded
        && state.project.projectChangeReasons.length > 0) {
        const list = state.project.projectChangeReasons.map((reason) => (
          <li key={reason}>{translateProjectChangeReason(reason)}</li>
        ));
        newAlertBanners.push(
          <QESAlert
            type="warning"
            key="projectChangeReasons"
            persist={true}
            collapseTitle={
              <>
                <strong>Warning!</strong> Your project results are out of date.
                {/* HTML Syntax doesn't allow a button inside a button (QESAlert's collapse button),
                      so we get around it here with a span with role="button" and the properties that otherwise would
                      be native to a button (onClick, onKeyDown, tabIndex) 
                      See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role for more
                  */}
                <span
                  className="d-inline-block ml-3"
                  onClick={() => onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId)}
                  onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.resource.resourceId); } }}
                  role="button"
                  tabIndex={0}
                  data-testid="zip-project-overview__run-project--results-out-of-date"
                >
                  <i className="fa fa-rocket" /> Run Now
                </span>
              </>
            }
          >
            <p className="pt5">The following changes have occurred since your project was last run:</p>
            <ul>
              {list}
            </ul>
          </QESAlert>
        )
      }

      if (state.project.canPublish && state.runConfiguration?.datePublished === undefined) {
        newAlertBanners.push(
          <QESAlert type="warning" key="projectUnpublished" persist={true}>
            <strong>Warning!</strong> Your project results are unpublished.
            <FlyoutLink
              classNames="ml-3"
              content={{
                body: <ZipProjectPublishFlyout onPublish={projectContext.onPublishTimeSlice} />,
                title: "Publish Options",
              }}
              testId="publish-project-flyout-link"
              disabled={state.loading}
              size="40"
            >
              <i className="fa fa-thumbs-o-up pr5" /> Publish
            </FlyoutLink>
          </QESAlert>
        );
      }

      if (state.runConfiguration?.status === ProjectRunConfigurationStatus.Failed
        && !state.project.isRunning) {
        newAlertBanners.push(
          <QESAlert type="warning" key="projectFailed" persist={true}>
            <strong>Warning!</strong> Your latest project run failed.
            <QESButton className="ml-3" qesType="secondary" onClick={() => onRunProject(dispatch, projectContext.onRunProject, projectContext.state.project.projectId)} qesTestId="zip-project-overview__run-project--run-failed"><i className="fa fa-rocket" /> Run Again</QESButton>
          </QESAlert>
        );
      }

      if (showProjectionMessage) {
        newAlertBanners.push(
          <RoleRestricted key="unprocessed-projections" allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers.
              {renderUnprocessedProvidersSidePanelLinkElement}
            </QESAlert>
          </RoleRestricted>
        );
      }
    }

    setAlertBanners(newAlertBanners);
  }, [state.runConfiguration, user, state.project]);

  return (
    alertBanners.length > 1
      ? (
        <QESAlertsContainer containerClass="mb-3">
          {alertBanners}
        </QESAlertsContainer>
      )
      : <>{alertBanners}</>
  )
}